import LogService from "services/LogService";
import { getCreditFlow } from "../../utils/creditUtils";
import useRedirectionStore from "../../zustandStores/redirectionStore";
import { ALL_TRANSACTIONS } from "../constants";
import FetchService from "../FetchService/CustomService";
import { getErrorMessage } from "services/utils";
import { getNewErrorMessage } from "services/utilsv2";
import CREDIT_EVENTS from "utils/CreditEvents";

const BaseUrl = process.env.BASE_URL;

const CreditService = {
  async userCreditProfile(recruiterId) {
    try {
      let url = BaseUrl + "/recruiter/profile/credits";
      const query = {};
      if (recruiterId) {
        query.recruiterId = recruiterId;
      }
      const loaderConfig = { hasLoader: false };
      const res = await FetchService.getCall({ url, query, loaderConfig });
      return res.data.data;
    } catch (err) {
      console.log("Error in UserCreditProfile", err);
      return Promise.reject(getErrorMessage(err));
    }
  },
  async allCrditSkus(
    requestFlow = "credit-purchase",
    totalCreditsNeeded = null,
    options = {}
  ) {
    try {
      const { loaderConfig = { hasLoader: true } } = options;
      let url = BaseUrl + "/recruiter/skus/metadata";
      if (totalCreditsNeeded) {
        url = url + `?credits=${totalCreditsNeeded}`;
      }
      const res = await FetchService.getCall({
        url,
        headers: {
          "request-flow": getCreditFlow(requestFlow)
        },
        loaderConfig
      });
      return res.data.data;
    } catch (err) {
      console.log("Error in AllCrditSkus", err);
      return Promise.reject(getErrorMessage(err));
    }
  },
  async gstInfo() {
    try {
      const url = BaseUrl + "/recruiter/transactions/gst-details";
      const res = await FetchService.getCall({ url });
      return res.data.data;
    } catch (err) {
      return Promise.reject(getErrorMessage(err));
    }
  },
  async getGstDetails({ gstNumber }) {
    try {
      const url =
        BaseUrl +
        `/recruiter/transactions/gst-detail-by-number?gst_number=${gstNumber}`;
      const res = await FetchService.getCall({
        url,
        loaderConfig: { hasLoader: false },
        query: { hideInterceptorMessage: true }
      });
      return res.data;
    } catch (error) {
      if (gstNumber && error?.response?.status === 429) {
        LogService.log(CREDIT_EVENTS.GST_POLLING_DAILY_LIMIT_REACHED, {
          gstNumber
        });
      }
      return Promise.reject(getNewErrorMessage(error));
    }
  },
  async totalCosting({ skuId, quantity }) {
    try {
      let trackingId = useRedirectionStore.getState().trackingId;
      const url = BaseUrl + `/recruiter/transactions/final-cost-details`;
      let query = {
        skuId: skuId,
        quantity: quantity || 1,
        hideInterceptorMessage: true
      };
      if (trackingId) query.trackingId = trackingId;
      const res = await FetchService.getCall({
        url,
        query: query
      });
      return res.data.data;
    } catch (err) {
      return Promise.reject(getErrorMessage(err));
    }
  },
  async initiateTransaction({
    skuId,
    quantity,
    gstNumber,
    isGstSelected,
    flow = "credit-purchase",
    orderId,
    jobId,
    trackingId,
    linkId,
    campaign,
    campaignSource
  }) {
    try {
      const url = BaseUrl + `/recruiter/transactions/initiate-transaction`;
      let payload = {
        skuId: Number(skuId),
        quantity: Number(quantity) || 1,
        gstNumber,
        isGstSelected,
        orderId,
        jobId: jobId ? Number(jobId) : undefined,
        trackingId: trackingId ? String(trackingId) : undefined,
        campaign: campaign ? campaign : undefined,
        campaignSource: campaignSource ? campaignSource : undefined
      };
      if (flow === "offline-purchase") {
        payload.linkId = linkId;
      }
      const res = await FetchService.postCall({
        url,
        headers: { "request-flow": getCreditFlow(flow) },
        body: payload
      });

      return res.data.data;
    } catch (err) {
      return Promise.reject(getErrorMessage(err));
    }
  },
  getTransaction: async ({ transactionId }) => {
    const url = BaseUrl + "/recruiter/transactions/transaction-template";

    try {
      const res = await FetchService.postCall({
        url,
        body: {
          transaction_id: transactionId
        }
      });
      return res.data.data.template;
    } catch (err) {
      // console.log("err", err);
      // return getErrorMessage(err);
      return Promise.reject(err);
    }
  },
  async TransactionStatus({ transactionId, source }) {
    try {
      let url = BaseUrl + `/recruiter/transactions/${transactionId}/get-status`;

      const res = await FetchService.getCall({
        url,
        query: { source }
      });

      return res.data;
    } catch (err) {
      console.log("Error in TransactionStatus", err);
      return Promise.reject(getErrorMessage(err));
    }
  },
  async SaveContactDetails(data, options = {}) {
    const url = BaseUrl + `/recruiter/feedback/contact-sales`;
    const { loaderConfig = { hasLoader: true } } = options;
    try {
      const res = await FetchService.postCall({
        url,
        body: {
          ...data
        },
        loaderConfig
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(error);
    }
  },
  async getTransactionHistory({
    dateId = 90,
    tabId = ALL_TRANSACTIONS,
    page = 1,
    recruiterId
  }) {
    try {
      let url = BaseUrl + `/recruiter/teams/transaction-history`;

      const res = await FetchService.getCall({
        url,
        query: {
          startDate: dateId,
          tabType: tabId,
          page: page - 1,
          recruiterId
        }
      });

      return res.data.data;
    } catch (err) {
      console.log("Error in TransactionHistory", err);
      return Promise.reject(getNewErrorMessage(err));
    }
  },
  upgradeJob: async ({ jobId }) => {
    const url = BaseUrl + "/recruiter/job/upgrade-to-premium";

    try {
      const res = await FetchService.postCall({
        url,
        body: {
          jobId: jobId
        }
      });
      return res.data.data;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  validateJobInfo: async ({ jobId, flow = "credit-purchase" }) => {
    const url = BaseUrl + "/recruiter/transactions/validate-app-link-purchase";

    try {
      let body = {
        jobId,
        flow: getCreditFlow(flow)
      };

      const res = await FetchService.postCall({
        url,
        body: body
      });
      return res.data.data;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  updateCreditEducation: async () => {
    const url = BaseUrl + "/recruiter/update-credit-education";

    try {
      const res = await FetchService.postCall({
        url,
        body: {
          source: "WEB"
        }
      });
      return res.data.data;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async offlinePurchaseCost({ linkId }) {
    try {
      const url = BaseUrl + `/recruiter/transactions/offline-purchase-details`;
      const res = await FetchService.getCall({
        url,
        query: { linkId, hideInterceptorMessage: true }
      });
      return res.data.data;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  async skusForSingleJob(params) {
    const { jobId, categoryId, cityId } = params;
    try {
      const url = BaseUrl + `/recruiter/skus/v2/jobs-metadata`;
      const response = await FetchService.getCall({
        url,
        query: {
          job_id: jobId,
          category_id: categoryId,
          city_id: cityId
        }
      });
      return response.data.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default CreditService;
